import { useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { useTransportMutation, useTransportQuery } from "@/transport"

import { isPersistedTestSetId } from "../utils/testSetTransportMapping"

export function useTestSetAutoAddRuleTransport(
  testSetId: string,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true
  return useTransportQuery({
    operation: "getTestSetAutoAddRule",
    request: { test_set_id: testSetId },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled: enabled && isPersistedTestSetId(testSetId),
      refetchOnMount: true,
      select: (data) => data.rule,
    },
  })
}

export function useTestSetAutoAddPendingTransport(
  testSetId: string,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true
  return useTransportQuery({
    operation: "listTestSetAutoAddPending",
    request: { test_set_id: testSetId },
    options: {
      ...CACHE_PROFILES.active,
      enabled: enabled && isPersistedTestSetId(testSetId),
      refetchOnMount: true,
      select: (data) => data.items,
    },
  })
}

export function useTestSetAutoAddSummaryTransport(
  testSetId: string,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true
  return useTransportQuery({
    operation: "getTestSetAutoAddSummary",
    request: { test_set_id: testSetId },
    options: {
      ...CACHE_PROFILES.active,
      enabled: enabled && isPersistedTestSetId(testSetId),
      refetchOnMount: true,
    },
  })
}

/**
 * Pending-review counts keyed by test set id for the test sets table. One
 * request per table; the badge hides when a test set has no entry.
 */
export function useTestSetAutoAddPendingCountsTransport(options?: {
  enabled?: boolean
}) {
  const enabled = options?.enabled ?? true
  const query = useTransportQuery({
    operation: "listTestSetAutoAddPendingCounts",
    request: {},
    options: {
      ...CACHE_PROFILES.active,
      enabled,
    },
  })
  const pendingCountsByTestSetId = useMemo(
    () => query.data?.pending_counts_by_test_set_id ?? {},
    [query.data],
  )
  return { ...query, pendingCountsByTestSetId }
}

export function useSaveTestSetAutoAddRuleTransport() {
  return useTransportMutation("saveTestSetAutoAddRule")
}

export function useDeleteTestSetAutoAddRuleTransport() {
  return useTransportMutation("deleteTestSetAutoAddRule")
}

export function useAcceptTestSetAutoAddSuggestionTransport() {
  return useTransportMutation("acceptTestSetAutoAddSuggestion")
}

export function useRejectTestSetAutoAddSuggestionTransport() {
  return useTransportMutation("rejectTestSetAutoAddSuggestion")
}
